import { Dispatch, useEffect, useState, ChangeEvent, useRef } from 'react'
import { fetchFixedPrivateRates, fetchInvestmentAccountRates } from '../api/httpHandlers'
import useDebounce from '../hooks/useDebounce'
import { track } from '../lib/tracking'
import { AppAction, AppState } from '../state/fixedAppState'
import CustomInput from './CustomInput'

function getMaxLengthWithSeparator(maxLengthAsNumber: number) {
  if (maxLengthAsNumber <= 3) return maxLengthAsNumber
  if (maxLengthAsNumber >= 4 && maxLengthAsNumber <= 6) return maxLengthAsNumber + 1
  if (maxLengthAsNumber >= 7 && maxLengthAsNumber <= 9) return maxLengthAsNumber + 2
  return maxLengthAsNumber + 3
}

interface Props {
  appState: AppState
  appDispatch: Dispatch<AppAction>
  variant: 'corporateFixed' | 'privateFixed'
}

export default function PlacementAmountInput({ appState, appDispatch, variant }: Readonly<Props>) {
  const { interestRateIndex, interestRateObjs, placementAmount, calculationState, userIsTyping } =
    appState
  const { maxAmount, minAmount } = interestRateObjs[interestRateIndex]
  const [userStoppedTyping, setUserStoppedTyping] = useState(true)

  // State for use with debounce
  const [immediatePlacementAmount, setImmediatePlacementAmount] = useState(placementAmount)
  // 500 ms default delay
  const debouncedPlacementAmount = useDebounce(immediatePlacementAmount)

  useEffect(() => {
    async function getInterestRates() {
      const { successful, data } =
        variant === 'corporateFixed'
          ? await fetchInvestmentAccountRates(debouncedPlacementAmount)
          : await fetchFixedPrivateRates(debouncedPlacementAmount)
      if (successful) {
        appDispatch({
          type: 'appDataFetched',
          interestRateObjs: data,
          placementAmount: debouncedPlacementAmount,
        })
      } else {
        appDispatch({ type: 'error' })
      }
    }
    // Return early if the debounced value is the same as in appState
    if (debouncedPlacementAmount === placementAmount)
      return appDispatch({ type: 'typing', value: false })
    if (debouncedPlacementAmount <= maxAmount && debouncedPlacementAmount >= minAmount) {
      getInterestRates()
    } else {
      appDispatch({ type: 'typing', value: false })
    }
  }, [debouncedPlacementAmount, userStoppedTyping])

  useEffect(() => {
    if (immediatePlacementAmount > maxAmount && calculationState !== 'high') {
      appDispatch({ type: 'calculationStateChange', value: 'high' })
    }
    if (immediatePlacementAmount < minAmount && calculationState !== 'low') {
      appDispatch({ type: 'calculationStateChange', value: 'low' })
    }
    if (
      immediatePlacementAmount >= minAmount &&
      immediatePlacementAmount <= maxAmount &&
      calculationState !== 'ok'
    ) {
      appDispatch({ type: 'calculationStateChange', value: 'ok' })
    }
  }, [immediatePlacementAmount, calculationState])

  const typingTimeout = useRef<NodeJS.Timeout | null>(null)

  function inputChangeHandler(event: ChangeEvent<HTMLInputElement>) {
    // Clear the old timeout
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current)
    }
    // Set userStoppedTyping to false when the user starts typing
    setUserStoppedTyping(false)
    // Set a timeout to set userStoppedTyping back to true after a delay
    typingTimeout.current = setTimeout(() => setUserStoppedTyping(true), 500)

    const formattedString = event.currentTarget.value.replaceAll(' ', '')
    const value = +formattedString
    if (immediatePlacementAmount === value) return
    if (!userIsTyping) appDispatch({ type: 'typing', value: true })
    track('change', event.currentTarget.getAttribute('data-label') ?? '')
    setImmediatePlacementAmount(value)
  }

  function isWithinLimits(value: number) {
    return value >= minAmount && value <= maxAmount
  }

  function getErrorMessage() {
    const verb = variant === 'corporateFixed' ? 'placera' : 'spara'
    if (immediatePlacementAmount > maxAmount) {
      return `Högsta belopp att ${verb} är ${maxAmount.toLocaleString('sv-SE')} kr.`
    }
    if (immediatePlacementAmount < minAmount) {
      return `Lägsta belopp att ${verb} är ${minAmount.toLocaleString('sv-SE')} kr.`
    }
    return ''
  }

  function formatMaxumumAmount(maxAmount: number) {
    let formattedNumber = maxAmount.toLocaleString('sv-SE')
    if (variant === 'corporateFixed') return formattedNumber
    if (maxAmount > 1000000 && maxAmount % 1000000 === 0) {
      const millions = maxAmount / 1000000
      formattedNumber = millions === 1 ? '1 miljon' : `${millions} miljoner`
    }

    return formattedNumber
  }

  return (
    <CustomInput
      id="placementAmount"
      label={variant === 'corporateFixed' ? 'Hur mycket vill ni placera?' : 'Belopp'}
      description={`Minst ${minAmount.toLocaleString('sv-SE')} kr och max ${formatMaxumumAmount(
        maxAmount,
      )} kr per konto.`}
      value={immediatePlacementAmount}
      inputChangeHandler={inputChangeHandler}
      suffix="kr"
      isValidHandler={(value: number) => isWithinLimits(value)}
      errorMessage={getErrorMessage()}
      maxLength={getMaxLengthWithSeparator(maxAmount.toString().length)}
      wide
    />
  )
}
